import React, { ForwardedRef, forwardRef } from "react";
import buttonStyles from "./button.module.css";
import { Button, SxProps, BoxProps, Box } from "@mui/material";
import { ButtonProps, ButtonTypeMap } from "@mui/material/Button/Button";
import { FormControlTooltip, FormControlTooltipProps } from "../tooltip";
import { CoreSpinner } from "../progress-indicators";

export type CoreButtonBaseProps<RootComponent extends React.ElementType = "button", AdditionalProps = object> = Omit<
	BoxProps<RootComponent, AdditionalProps>,
	"ref"
> &
	Pick<FormControlTooltipProps, "tooltip" | "disabledTooltip" | "slotProps"> & {
		ref?: React.ComponentPropsWithRef<RootComponent>["ref"];
	};

interface CoreButtonBaseComponent {
	<RootComponent extends React.ElementType, AdditionalProps = object>(
		props: { component: RootComponent } & CoreButtonBaseProps<RootComponent, AdditionalProps>,
	): React.ReactElement;

	(props: CoreButtonBaseProps): React.ReactElement;
}

export const CoreButtonBase = forwardRef(function CoreButtonBase(
	{
		tooltip,
		disabledTooltip,
		className,
		children,
		slotProps,
		"aria-label": ariaLabel,
		component = "button",
		...boxProps
	}: CoreButtonBaseProps,
	ref: ForwardedRef<HTMLButtonElement>,
) {
	return (
		<FormControlTooltip tooltip={tooltip} disabledTooltip={disabledTooltip} slotProps={slotProps}>
			<Box
				component={component}
				type={component === "button" ? "button" : undefined}
				{...boxProps}
				className={`${buttonStyles["button-base"]} ${className || ""}`}
				aria-label={ariaLabel ?? (typeof tooltip === "string" ? tooltip : undefined)}
				ref={ref}
			>
				{children}
			</Box>
		</FormControlTooltip>
	);
}) as CoreButtonBaseComponent;

declare module "@mui/material/Button" {
	interface ButtonPropsColorOverrides {
		primary: true;
		secondary: true;
		cancel: true;
		success: false;
		error: false;
		info: false;
		warning: false;
		inherit: false;
	}
}

/** The color of the component. */
export type CoreButtonColors = "primary" | "secondary" | "cancel";

/** Custom color that overrides the core colors */
export type CoreButtonCustomColor = string | { default: string; hover?: string; active?: string; disabled?: string };

function getCustomColorString(
	customColor: CoreButtonCustomColor,
	type: "default" | "hover" | "active" | "disabled",
): string {
	if (typeof customColor === "string") {
		return customColor;
	} else if (Object.hasOwn(customColor, type)) {
		return customColor[type] ?? customColor.default;
	}
	return customColor.default;
}

export type CoreButtonProps<
	RootComponent extends React.ElementType = ButtonTypeMap["defaultComponent"],
	AdditionalProps = object,
> = {
	/** The style of the button. @default "outlined" */
	variant?: "contained" | "outlined";
	/** If `true`, the modal will use an older style. @default false */
	old?: boolean;
	/** Callback fired when the component is clicked. */
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	/** The color of the component. @default "primary" */
	color?: CoreButtonColors;
	/** An color that will replace the color in `color`.
	 * Can be a string or object containing properties `default`, `hover`, `active`, and `disabled`.
	 */
	customColor?: CoreButtonCustomColor;
	/** If `true`, the focus outline will not be shown. @default false */
	hideOutline?: boolean;
	/** If `true`, focusing on elements within the button will display the focus styling of the button. @default false*/
	focusWithin?: boolean;
	/** If `true`, the button will always be displayed in its 'hover' style ('toggled on') */
	forceHoverStyle?: boolean;
	/** If `true`, the button will have a highlight when hovered (useful for buttons with customColors) */
	hoverHighlight?: boolean;
	/** If `true`, the button will apply the font color to the svg path fill */
	applyColorToSvg?: boolean;
	/** If `true`, the button will always apply the background color */
	checked?: boolean;
	/** If `true`, disables the background color when the button is solely focused*/
	disableFocusBackground?: boolean;
	/** Sets cursor to 'not-allowed' for disabled buttons */
	disabledCursor?: boolean;
	/** If `true`, a loading spinner will be shown */
	loading?: boolean;
	/** Position of the loading spinner */
	loadingStyle?: "startSpinner" | "endSpinner" | "ellipsis";
} & Omit<ButtonProps<RootComponent, AdditionalProps>, "variant" | "color" | "component"> &
	Pick<FormControlTooltipProps, "tooltip" | "disabledTooltip" | "slotProps">;

interface CoreButtonComponent {
	<RootComponent extends React.ElementType, AdditionalProps = object>(
		props: { component: RootComponent } & CoreButtonProps<RootComponent, AdditionalProps>,
	): React.ReactElement;

	(props: CoreButtonProps): React.ReactElement;
}

export const CoreButton = forwardRef(function CoreButton(
	{
		variant = "outlined",
		old = false,
		children,
		color = "primary",
		onClick,
		customColor,
		hideOutline = true,
		focusWithin,
		forceHoverStyle = false,
		hoverHighlight,
		applyColorToSvg = false,
		checked = false,
		size = "small",
		disableFocusBackground,
		tooltip,
		disabledTooltip,
		disabledCursor,
		loading = false,
		loadingStyle = "startSpinner",
		slotProps,
		startIcon,
		endIcon,
		disabled,
		"aria-label": ariaLabel,
		...props
	}: CoreButtonProps,
	ref: ForwardedRef<HTMLButtonElement>,
) {
	let colorClass = buttonStyles["btn--primary"];
	switch (color) {
		case "primary":
			colorClass = buttonStyles["btn--primary"];
			break;
		case "secondary":
			colorClass = buttonStyles["btn--secondary"];
			break;
		case "cancel":
			colorClass = buttonStyles["btn--cancel"];
			break;
	}

	const colorSx: SxProps<typeof Button> | undefined = customColor
		? {
				"&": {
					color: getCustomColorString(customColor, "default"),
					borderColor: getCustomColorString(customColor, "default"),
					...(applyColorToSvg && { [`&.svg path`]: { fill: getCustomColorString(customColor, "default") } }),
					...(checked && {
						color: "#f9f9f9",
						backgroundColor: getCustomColorString(customColor, "hover"),
						borderColor: getCustomColorString(customColor, "hover"),
						...(applyColorToSvg && { [`&.svg path`]: { fill: "#f9f9f9" } }),
					}),
				},
				[`&:hover, &:focus:not(.btn-outlined)${focusWithin ? "&:focus-within" : ""}`]: {
					color: "#f9f9f9",
					backgroundColor: getCustomColorString(customColor, "hover"),
					borderColor: getCustomColorString(customColor, "hover"),
					...(applyColorToSvg && { [`&.svg path`]: { fill: "#f9f9f9" } }),
				},
				"&:active": {
					color: "#f9f9f9",
					backgroundColor: getCustomColorString(customColor, "active"),
					borderColor: getCustomColorString(customColor, "active"),
					...(applyColorToSvg && { [`&.svg path`]: { fill: "#f9f9f9" } }),
				},
				"&:disabled": {
					color: getCustomColorString(customColor, "default"),
					backgroundColor: getCustomColorString(customColor, "disabled"),
					borderColor: getCustomColorString(customColor, "disabled"),
					...(applyColorToSvg && { [`&.svg path`]: { fill: "#54476b" } }),
				},
				...(disableFocusBackground &&
					!checked && {
						"&:focus-visible:not(:hover):not(.btn-outlined)": {
							backgroundColor: "inherit",
							borderColor: getCustomColorString(customColor, "default"),
							color: getCustomColorString(customColor, "default"),
						},
					}),
			}
		: undefined;
	if (colorSx) {
		colorClass = "";
	}

	const classNames = [buttonStyles["core-btn"], buttonStyles[size], buttonStyles[variant], colorClass];

	// Optional classes
	if (!hideOutline) classNames.push(buttonStyles["btn-outlined"]);
	if (checked) classNames.push(buttonStyles["core-btn-checked"]);
	if (focusWithin) classNames.push(buttonStyles["btn-focus-within"]);
	if (forceHoverStyle) classNames.push(buttonStyles["hover"]);
	if (applyColorToSvg) classNames.push(buttonStyles["svg"]);
	if (hoverHighlight) classNames.push(buttonStyles["btn-hover-highlight"]);
	if (disableFocusBackground) classNames.push(buttonStyles["btn-no-focus-background"]);
	if (disabledCursor) classNames.push(buttonStyles["btn-disabled-cursor"]);
	if (props.className) classNames.push(props.className);

	const finalClassName = classNames.join(" ");

	return (
		<FormControlTooltip tooltip={tooltip} disabledTooltip={disabledTooltip} slotProps={slotProps}>
			<Button
				{...props}
				onClick={onClick}
				disabled={disabled || loading}
				className={finalClassName}
				startIcon={
					loading && loadingStyle === "startSpinner" ? <CoreSpinner size={size === "small" ? 20 : 24} /> : startIcon
				}
				endIcon={loading && loadingStyle === "endSpinner" ? <CoreSpinner size={size === "small" ? 20 : 24} /> : endIcon}
				data-theme={old ? "old" : ""}
				sx={{ ...props.sx, ...colorSx }}
				ref={ref}
				disableFocusRipple
				disableTouchRipple
				aria-label={ariaLabel ?? (typeof tooltip === "string" ? tooltip : undefined)}
			>
				{children}
				{loading && loadingStyle === "ellipsis" && "..."}
			</Button>
		</FormControlTooltip>
	);
}) as CoreButtonComponent;

export type CoreIconButtonProps<
	RootComponent extends React.ElementType = ButtonTypeMap["defaultComponent"],
	AdditionalProps = object,
> = (
	| ({ variant?: "outlined" | "contained" } & CoreButtonProps<RootComponent, AdditionalProps>)
	| ({
			variant: "plain";
			color?: string;
	  } & CoreButtonBaseProps<RootComponent, AdditionalProps>)
) &
	Pick<CoreButtonBaseProps, "tooltip" | "disabledTooltip" | "slotProps"> & {
		forceActiveStyle?: boolean;
		size?: "small" | "medium" | "large";
	};

interface CoreIconButtonComponent {
	<RootComponent extends React.ElementType, AdditionalProps = object>(
		props: { component: RootComponent } & CoreIconButtonProps<RootComponent, AdditionalProps>,
	): React.ReactElement;

	(props: CoreIconButtonProps): React.ReactElement;
}

export const CoreIconButton = forwardRef<HTMLButtonElement, CoreIconButtonProps>(function CoreIconButton(props, ref) {
	const { forceActiveStyle, className, ...remainingProps } = props;

	if (!remainingProps.variant || remainingProps.variant === "outlined" || remainingProps.variant === "contained") {
		const { variant: _variant, ...buttonProps } = remainingProps;
		return <CoreButton {...buttonProps} className={`${buttonStyles["icon"]} ${className ?? ""}`} ref={ref} />;
	} else {
		const { color = "primary", size = "small", variant: _variant, sx, ...buttonProps } = remainingProps;
		let resolvedColor: string | undefined = color;
		if (color === "primary") {
			resolvedColor = "#42296e";
		} else if (color === "dark") {
			resolvedColor = undefined;
		}
		return (
			<CoreButtonBase
				{...buttonProps}
				className={`${buttonStyles["icon-button"]} ${buttonStyles[size]} ${forceActiveStyle ? buttonStyles.active : ""} ${color === "dark" ? buttonStyles.dark : ""} ${className ?? ""}`}
				sx={{ color: resolvedColor, ...sx }}
				ref={ref}
			/>
		);
	}
}) as CoreIconButtonComponent;
